/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * MfaRecommendPage — Recommends MFA setup after invite acceptance
 * ============================================================================
 *
 * Shown after a new org user accepts their invite and sets a password
 * when MFA is not enforced. Offers the option to set up MFA voluntarily
 * or skip to the dashboard.
 *
 * ============================================================================
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/stores/authStore';
import type { UserRole } from '@/types/auth.types';

function getDefaultRoute(role: UserRole): string {
  return role === 'SUPER_ADMIN' ? '/superadmin' : '/venta';
}

export function MfaRecommendPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const handleSetupMfa = () => {
    navigate('/auth/mfa/setup');
  };

  const handleSkip = () => {
    navigate(getDefaultRoute(user?.role as UserRole ?? 'VIEWER'));
  };

  return (
    <AuthLayout
      title={t('mfaRecommend.title')}
      subtitle={t('mfaRecommend.subtitle')}
      icon={<ShieldCheck size={28} />}
      iconVariant="teal"
      maxWidth="sm"
    >
      <div className="mfa-recommend">
        <div className="mfa-recommend-benefits">
          <p className="mfa-recommend-description">
            {t('mfaRecommend.description')}
          </p>
          <ul className="mfa-recommend-list">
            <li>{t('mfaRecommend.benefit1')}</li>
            <li>{t('mfaRecommend.benefit2')}</li>
            <li>{t('mfaRecommend.benefit3')}</li>
          </ul>
        </div>

        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          onClick={handleSetupMfa}
          glow
        >
          {t('mfaRecommend.setupButton')}
        </Button>

        <button
          type="button"
          className="mfa-recommend-skip"
          onClick={handleSkip}
        >
          {t('mfaRecommend.skipButton')}
        </button>
      </div>

      <style>{`
        .mfa-recommend {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .mfa-recommend-benefits {
          padding: 20px;
          background: rgba(13, 148, 136, 0.08);
          border: 1px solid rgba(13, 148, 136, 0.2);
          border-radius: var(--radius-lg);
        }

        [data-theme="light"] .mfa-recommend-benefits {
          background: rgba(13, 148, 136, 0.05);
          border-color: rgba(13, 148, 136, 0.15);
        }

        .mfa-recommend-description {
          font-size: var(--text-sm);
          color: var(--color-text-secondary);
          margin: 0 0 14px 0;
          line-height: 1.6;
        }

        .mfa-recommend-list {
          margin: 0;
          padding-left: 20px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .mfa-recommend-list li {
          font-size: var(--text-sm);
          color: var(--color-text-secondary);
          line-height: 1.5;
        }

        .mfa-recommend-list li::marker {
          color: #0D9488;
        }

        .mfa-recommend-skip {
          background: none;
          border: none;
          color: var(--color-text-muted);
          font-size: var(--text-sm);
          cursor: pointer;
          padding: 8px;
          text-align: center;
          transition: color 0.15s;
        }

        .mfa-recommend-skip:hover {
          color: var(--color-text-secondary);
          text-decoration: underline;
        }

        @media (max-width: 640px) {
          .mfa-recommend {
            gap: 1rem;
          }
          .mfa-recommend-benefits {
            padding: 1rem;
          }
        }
      `}</style>
    </AuthLayout>
  );
}

export default MfaRecommendPage;
